// The quote page's re-entry screen: how a visitor whose link no longer
// opens the quote gets back in without knowing whether they have an
// account. The pure rules live here: which mode the screen is in, what its
// buttons say, the disclosure under them, and how the server's answers and
// the proof's outcomes are read. The requests are in reentry-client.

import { z } from "zod";

import {
  consentDisclosure,
  type ConsentDisclosure,
} from "helpers/consent-disclosure";
import {
  parseGrantLookup,
  type GrantCompletion,
  type GrantLookup,
  type GrantMethod,
} from "pages/Login/login-grant-flow";
import type { SentByGrant } from "pages/Login/grant-send";
import type {
  ConfirmFailureReason,
  ConfirmTrigger,
} from "pages/StartLead/events";
import type { SignInFailure } from "pages/StartLead/signIn";

/** `on_file`: the destinations come from what the link was minted for and
 * are shown masked. `typed`: the visitor types where to send it. */
export type ReentryMode = "on_file" | "typed";

/** The channels the server offers for the link's recipient, as masked
 * displays, and whether that person has an After account. */
export interface ReentryOptions {
  sms: string | null;
  email: string | null;
  hasAccount: boolean;
}

export const NO_REENTRY_OPTIONS: ReentryOptions = {
  sms: null,
  email: null,
  hasAccount: false,
};

const destinationSchema = z
  .object({ destination: z.string().min(1) })
  .nullable()
  .optional();

const optionsSchema = z.object({
  sms: destinationSchema,
  email: destinationSchema,
  has_account: z.boolean().optional(),
});

/** Reads the options answer defensively: anything that is not a usable
 * answer offers nothing, which puts the screen in `typed` mode. */
export function parseReentryOptions(raw: unknown): ReentryOptions {
  const parsed = optionsSchema.safeParse(raw);
  if (!parsed.success) return NO_REENTRY_OPTIONS;
  const { sms, email, has_account } = parsed.data;
  return {
    sms: sms?.destination ?? null,
    email: email?.destination ?? null,
    hasAccount: has_account === true,
  };
}

const RECIPIENT_REFUSALS: ReadonlySet<string> = new Set([
  "quote_link_redeemed",
  "quote_link_expired",
]);

/** The link token worth asking the server about: only one the quote has
 * refused as spent or expired can name a recipient. A revoked link, a
 * bookmark and a signature handoff are never asked about. */
export function recipientLookupToken(
  reason: string,
  linkToken: string | null,
): string | null {
  return linkToken !== null && RECIPIENT_REFUSALS.has(reason)
    ? linkToken
    : null;
}

/** How a mode came on show: `arrival` for the one the screen opened on,
 * `switch` for any the visitor or the screen moved to afterwards. */
export type ReentryViewEntry = "arrival" | "switch";

export function reentryViewEntryFor(
  previous: ReentryMode | null,
): ReentryViewEntry {
  return previous === null ? "arrival" : "switch";
}

export function reentryModeOf(options: ReentryOptions): ReentryMode {
  return options.sms !== null || options.email !== null ? "on_file" : "typed";
}

/** The method the screen leads with: a texted code when one can be sent,
 * else the emailed link. Null when neither can. */
export function leadingMethodOf(options: ReentryOptions): GrantMethod | null {
  if (options.sms !== null) return "sms_otp";
  return options.email !== null ? "email_link" : null;
}

/** The method offered beside the leading one, when both channels exist. */
export function alternativeMethodOf(
  options: ReentryOptions,
): GrantMethod | null {
  return options.sms !== null && options.email !== null ? "email_link" : null;
}

export function destinationFor(
  options: ReentryOptions,
  method: GrantMethod,
): string | null {
  return method === "sms_otp" ? options.sms : options.email;
}

/** The label on a send button. With no method yet (nothing valid typed) it
 * only says to go on. */
export function sendLabelFor(method: GrantMethod | null): string {
  if (method === null) return "Continue";
  return method === "sms_otp" ? "Text me a code" : "Email me a link";
}

export const ALTERNATIVE_EMAIL_LABEL = "Email me a link instead";

function lastFourDigitsOf(maskedPhone: string): string | null {
  const match = /(\d{4})\D*$/.exec(maskedPhone);
  return match ? match[1] : null;
}

/** The disclosure under a send button. A texted code also asks marketing-
 * text consent for the number it goes to: the one on file, named by its
 * last four digits, or the one the visitor typed. */
export function reentryDisclosure(input: {
  method: GrantMethod | null;
  destination: ReentryMode;
  hasAccount: boolean;
  /** The masked phone on file, for the number the consent names. */
  maskedPhone: string | null;
}): ConsentDisclosure {
  const action = sendLabelFor(input.method);
  if (input.method !== "sms_otp") {
    return consentDisclosure({
      action,
      hasAccount: input.hasAccount,
      texting: null,
    });
  }
  const lastFour =
    input.destination === "on_file" && input.maskedPhone !== null
      ? lastFourDigitsOf(input.maskedPhone)
      : null;
  return consentDisclosure({
    action,
    hasAccount: input.hasAccount,
    texting:
      input.destination === "typed"
        ? { kind: "entered" }
        : lastFour === null
          ? { kind: "provided" }
          : { kind: "on_file", lastFour },
  });
}

/** What prompted the first send from this screen: the leading button is the
 * screen's own, the other one is a switch. A send after the code screen is
 * on show reports its own trigger. */
export function firstSendTrigger(
  method: GrantMethod,
  leading: GrantMethod | null,
): ConfirmTrigger {
  return method === leading ? "automatic" : "switch_method";
}

const GRANT_TOKEN = /^[0-9a-f]{64}$/;

const startedSchema = z.object({
  login_grant: z.string().regex(GRANT_TOKEN),
  methods: z.array(z.string()).optional(),
  phone_display: z.string().nullable().optional(),
  email_display: z.string().nullable().optional(),
  sent: z.boolean().optional(),
  session_info: z.string().min(1).optional(),
});

export type StartedReentry = {
  loginGrant: string;
  lookup: GrantLookup;
  /** The send the server already made for this grant, when it made one
   * the code screen can resume on. Null leaves the send to the screen. */
  sent: SentByGrant | null;
};

export function reentryPath(quoteId: string): string {
  return `/quotes/${quoteId}`;
}

/** Reads the answer to asking for a code or link to a destination on
 * file. The grant it names signs in to this quote; a display the answer
 * leaves out is the one offered for the method that was asked for. A send is resumed only
 * when the answer says it was made and, for a texted code, carries the
 * verification session the code is checked against. */
export function parseStartedReentry(
  raw: unknown,
  quoteId: string,
  method: GrantMethod,
  options: ReentryOptions,
): StartedReentry | null {
  const parsed = startedSchema.safeParse(raw);
  if (!parsed.success) return null;
  const body = parsed.data;
  const lookup = parseGrantLookup({
    purpose: "quote_reentry",
    methods: body.methods ?? [method],
    phone_display:
      body.phone_display ?? (method === "sms_otp" ? options.sms : null),
    email_display:
      body.email_display ?? (method === "email_link" ? options.email : null),
    redirect_path: reentryPath(quoteId),
  });
  if (lookup === null) return null;
  return { loginGrant: body.login_grant, lookup, sent: sentOf(body, method) };
}

function sentOf(
  body: z.infer<typeof startedSchema>,
  method: GrantMethod,
): SentByGrant | null {
  if (body.sent !== true) return null;
  if (method === "email_link") return { method };
  return body.session_info === undefined
    ? null
    : { method, sessionInfo: body.session_info };
}

/** Why asking for a code or link to a destination on file was refused. */
export type ReentryFailure =
  "rate_limited" | "unavailable" | "link_unknown" | "other";

export function reentrySendFailure(
  statusCode: number | undefined,
): ReentryFailure {
  if (statusCode === 429) return "rate_limited";
  if (statusCode === 422) return "unavailable";
  if (statusCode === 404) return "link_unknown";
  return "other";
}

/** The line shown for a refused send. */
export function reentryFailureCopy(failure: ReentryFailure): string {
  const copy: Record<ReentryFailure, string> = {
    rate_limited:
      "That didn’t go through. Please wait a moment and try again, or give us a call.",
    unavailable:
      "We can’t send that right now. Try the other way, or give us a call.",
    link_unknown: "This link can’t be used. Enter your email or mobile phone.",
    other: "Something went wrong. Please try again.",
  };
  return copy[failure];
}

/** The reasons a re-entry reports as failed: this screen's own, the typed
 * sign-in's, and the code screen's. */
export type ReentryFailureReason =
  ReentryFailure | SignInFailure | ConfirmFailureReason;

export const NOT_FOUND_COPY =
  "We couldn’t find this quote for that email or phone. Try the one it was sent to, or call us.";

/** What a proven channel that did not sign in comes to on a quote page. A
 * channel nobody holds is never offered an account here: the visitor came
 * for this quote, so it reads as not found. */
export type ProofOutcome =
  { kind: "not_found" } | { kind: "use_account_channel" };

export function proofOutcomeOf(
  completion: Extract<
    GrantCompletion,
    { kind: "create_account" | "use_account_channel" | "not_found" }
  >,
): ProofOutcome {
  return completion.kind === "use_account_channel"
    ? { kind: "use_account_channel" }
    : { kind: "not_found" };
}

/** The router state a sign-in that came from a re-entry link carries to
 * the quote it leads to, so the page can say when the quote still did not
 * open for that person. */
export const REENTRY_PROOF_STATE = { quoteReentry: true } as const;

export function readReentryProof(state: unknown): boolean {
  return (
    typeof state === "object" &&
    state !== null &&
    (state as Record<string, unknown>).quoteReentry === true
  );
}

/** The router state a re-entry link carries back to its quote when the
 * channel it proved reaches nobody who may open the quote. Nobody is
 * signed in by it, so it carries no proof to exchange. */
export const REENTRY_NOT_FOUND_STATE = { quoteReentryNotFound: true } as const;

export function readReentryNotFound(state: unknown): boolean {
  return (
    typeof state === "object" &&
    state !== null &&
    (state as Record<string, unknown>).quoteReentryNotFound === true
  );
}
